import {
  ActionIcon,
  Box,
  Button,
  Center,
  Container,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { ReactElement, useMemo, useState } from 'react';
import { IconPlus, IconUpload, IconDownload, IconEye, IconSearch } from '@tabler/icons-react';
import { CreateFaxForm } from './CreateFaxForm';
import { FaxContent } from './FaxContent';
import { Communication, Organization, Patient } from '@medplum/fhirtypes';
import { FaxDirection } from '@/services/faxing';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import {
  createReference,
  formatHumanName,
  getExtensionValue,
  getReferenceString,
  isResource,
  resolveId,
} from '@medplum/core';
import { InboundIcon } from '@/components/icons/InboundIcon';
import { OutboundIcon } from '@/components/icons/OutboundIcon';
import { alert } from '@/services/alert';
import { useLiveData } from '@/hooks/useLiveData';
import useLoadMoreOnScroll from '@/hooks/useLoadMoreOnScroll';
import dayjs from 'dayjs';
import { useMedplum } from '@medplum/react';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { useSearchParams, Link } from 'react-router';

// Return more information to improve search
const getSenderAccessor = (communication: Communication): string => {
  const displayName = communication.sender && communication.sender.display ? communication.sender.display : 'Unknown';
  const originatingFaxNumber = getExtensionValue(communication, 'https://efax.com/originating-fax-number');
  const faxNumber = originatingFaxNumber && typeof originatingFaxNumber === 'string' ? originatingFaxNumber : 'Unknown';
  return `${displayName} ${faxNumber} ${formatPhoneNumber(faxNumber)}`;
};

const getSenderFaxNumber = (communication: Communication): string => {
  if (communication.sender && communication.sender.display) {
    return communication.sender.display;
  }
  const originatingFaxNumber = getExtensionValue(communication, 'https://efax.com/originating-fax-number');
  if (originatingFaxNumber && typeof originatingFaxNumber === 'string') {
    return formatPhoneNumber(originatingFaxNumber);
  }
  return 'Unknown';
};

const getPatientDisplayName = (communication: Communication): string => {
  if (communication.subject?.resource) {
    const patient = communication.subject.resource as Patient;
    return formatHumanName(patient?.name?.[0]) ?? 'Unknown';
  }
  return communication.subject?.display ?? 'N/A';
};

const getRecipientDisplayName = (communication: Communication): string => {
  const recipient = communication.recipient?.[0];
  if (recipient?.resource) {
    if (isResource(recipient.resource, 'Organization')) {
      return (recipient.resource as Organization).name ?? 'Unknown';
    }
    if (isResource(recipient.resource, 'Practitioner')) {
      return formatHumanName((recipient.resource as any).name?.[0]) ?? 'Unknown';
    }
  }
  return recipient?.display ?? 'Unknown';
};

export const FAX_SEARCH_QUERY_PARAM = 'search';

export function FaxingPage(): ReactElement {
  const medplum = useMedplum();
  const [searchParams] = useSearchParams();
  const [directionFilter, setDirectionFilter] = useState<FaxDirection>('inbound');
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [viewModalOpened, setViewModalOpened] = useState(false);
  const [selectedFax, setSelectedFax] = useState<Communication | null>(null);
  const [globalFilter, setGlobalFilter] = useState(() => searchParams.get(FAX_SEARCH_QUERY_PARAM) || '');

  const queryParams = useMemo(
    () => ({
      category: directionFilter,
      _sort: '-sent',
      status: 'completed,in-progress',
    }),
    [directionFilter]
  );

  const {
    data: faxes,
    loading,
    refresh,
    loadMore,
    loadingMore,
    hasMore,
  } = useLiveData<'Communication', Communication>('Communication', queryParams, {
    enablePagination: true,
    pageSize: 200,
    addNewItemToTop: true,
  });
  const handleScroll = useLoadMoreOnScroll({ hasMore, loadingMore, loading, loadMore });

  const handleModalClose = () => {
    setCreateModalOpened(false);
    refresh();
  };

  const handleOpenFax = (fax: Communication) => {
    setSelectedFax(fax);
    setViewModalOpened(true);
  };

  const handlePatientChanged = async (patient?: Patient) => {
    if (!selectedFax) return;

    // Fax had no subject and none was selected
    if (!patient && !selectedFax.subject) return;

    // Fax had a subject and the same was selected
    if (patient && selectedFax.subject) {
      const patientReference = getReferenceString(patient);
      if (patientReference === selectedFax.subject.reference) return;
    }

    const updatedFax = { ...selectedFax, subject: patient ? createReference(patient) : undefined };
    try {
      await medplum.updateResource(updatedFax);
      setSelectedFax(updatedFax);
    } catch (error) {
      alert.error(`Couldn't set patient for fax: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const columns = useMemo<MRT_ColumnDef<Communication>[]>(() => {
    return [
      {
        id: 'type',
        header: '',
        enableSorting: false,
        size: 60,
        enableGlobalFilter: false,
        accessorFn: (row) => row,
        Cell: () => {
          return (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#223843',
                }}
              >
                {directionFilter === 'inbound' ? <InboundIcon size={16} /> : <OutboundIcon size={16} />}
              </Box>
            </Box>
          );
        },
      },
      {
        id: 'contact',
        header: 'Contact',
        size: 200,
        enableSorting: false,
        accessorFn: (fax) => (directionFilter === 'outbound' ? getRecipientDisplayName(fax) : getSenderAccessor(fax)),
        Cell: ({ row }) => {
          const communication = row.original;
          const organizationId =
            directionFilter === 'inbound' ? resolveId(communication.sender) : resolveId(communication.recipient?.[0]);
          const displayText =
            directionFilter === 'outbound' ? getRecipientDisplayName(communication) : getSenderFaxNumber(communication);

          if (organizationId) {
            return (
              <Text
                size="sm"
                component={Link}
                to={`/Organizations/${organizationId}/timeline`}
                style={{ color: '#25639A' }}
              >
                {displayText}
              </Text>
            );
          }

          return <Text size="sm">{displayText}</Text>;
        },
      },
      {
        id: 'sent',
        header: 'Date',
        size: 150,
        enableSorting: true,
        sortingFn: 'datetime',
        accessorFn: (fax) => fax.sent,
        Cell: ({ cell }) => {
          const sentDate = cell.getValue<Date>();
          return <Text size="sm">{sentDate ? dayjs(sentDate).fromNow() : '--'}</Text>;
        },
      },
      {
        id: 'patient',
        header: 'Patient',
        size: 200,
        enableSorting: false,
        accessorFn: (fax) => getPatientDisplayName(fax),
        Cell: ({ row }) => {
          const communication = row.original;
          const patientId = resolveId(communication.subject);
          const displayText = getPatientDisplayName(communication);

          if (patientId) {
            return (
              <Text size="sm" component={Link} to={`/Patient/${patientId}/timeline`} style={{ color: '#25639A' }}>
                {displayText}
              </Text>
            );
          }

          return <Text size="sm">{displayText}</Text>;
        },
      },
      {
        id: 'actions',
        header: '',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box
            px={10}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              width: 'fit-content',
              borderRadius: '10px',
              border: '1px solid #e0e0e0',
              backgroundColor: 'white',
            }}
            py={5}
            onClick={(e) => e.stopPropagation()}
          >
            <Group gap="xs">
              <Tooltip label="View">
                <ActionIcon variant="subtle" color="dark" size="xs" onClick={() => handleOpenFax(row.original)}>
                  <IconEye size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>
        ),
      },
    ];
  }, [directionFilter]);

  const table = useMantineReactTable({
    columns,
    data: faxes,
    rowCount: faxes.length,
    globalFilterFn: 'contains',
    initialState: {
      density: 'xs' as const,
      sorting: [{ id: 'sent', desc: true }],
    },
    state: {
      isLoading: loading,
      showGlobalFilter: false,
      globalFilter: globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    enableRowSelection: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowVirtualization: true,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    mantineTableProps: {
      highlightOnHover: true,
    },
    mantineTableBodyRowProps: () => ({
      style: { cursor: 'default' },
    }),
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: {
      style: {
        flex: 1,
        overflow: 'auto',
      },
      onScroll: handleScroll,
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
  });

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Faxing</Title>
        </Group>
        <Group justify="space-between">
          <SegmentedControl
            radius="lg"
            size="md"
            value={directionFilter}
            onChange={(value) => setDirectionFilter(value as FaxDirection)}
            withItemsBorders={false}
            data={[
              {
                value: 'inbound',
                label: (
                  <Center style={{ gap: 10 }}>
                    <IconDownload size={16} />
                    <span>Inbound</span>
                  </Center>
                ),
              },
              {
                value: 'outbound',
                label: (
                  <Center style={{ gap: 10 }}>
                    <IconUpload size={16} />
                    <span>Outbound</span>
                  </Center>
                ),
              },
            ]}
          />
          <Group gap="md" flex="space-between">
            <TextInput
              placeholder="Search"
              leftSection={<IconSearch size={16} />}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.currentTarget.value)}
              style={{ width: 300 }}
              radius="lg"
            />
            <Button radius="lg" variant="outline" color="gray" onClick={() => setCreateModalOpened(true)}>
              <Group gap="xs">
                <IconPlus size={16} />
                New
              </Group>
            </Button>
          </Group>
        </Group>

        <Paper style={{ flex: 1, overflow: 'hidden' }}>
          <MantineReactTable table={table} />
        </Paper>
      </Stack>

      <Modal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        title="Outgoing fax"
        centered
        size="xl"
      >
        <CreateFaxForm onClose={handleModalClose} />
      </Modal>

      <FaxContent
        opened={viewModalOpened}
        onClose={async (patient?: Patient) => {
          await handlePatientChanged(patient);
          setViewModalOpened(false);
          setSelectedFax(null);
        }}
        fax={selectedFax}
      />
    </Container>
  );
}
